import classnames from 'classnames';
import omit from 'omit.js';
import React, { CSSProperties, ReactElement, ReactNode, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { prefix, setComponentSize, WebUI } from "../../wui-core/src/index"
import { RadioGroupProps, RadioValue, MockRadio, RadioProps } from './iRadio';
import Radio, { RadioGroupContext } from './Radio';
import RadioButton from './RadioButton';
import Dropdown from '../../wui-dropdown/src';
import Popover from '../../wui-popover/src';
import Menu from '../../wui-menu/src';
import Button from '../../wui-button/src';
import Icon from '../../wui-icon/src';
import { OrNull } from '../../wui-core/src/types/iCore';
import { getLangInfo } from '../../wui-locale/src/tool';
import i18n from '../../wui-calendar/src/i18n/lang';
import { WithConfigConsumer } from '../../wui-provider/src/context'
const { Item } = Menu;

const sizeMap = {
    sm: 'sm',
    md: 'md',
    lg: 'lg',
    small: 'sm',
    middle: 'md',
    large: 'lg',
}
const noop = () => {
}

const getValues = (children: any): RadioValue[] => {
    let array: RadioValue[] = []
    if (!children || (Array.isArray(children) && children.length === 0)) {
        return array;
    }
    if (!Array.isArray(children)) {
        children = [children]
    } else if (children.some((child: any) => Array.isArray(child))) {
        let res: (React.ReactElement | MockRadio)[] = []
        children.forEach((child: any) => {
            if (Array.isArray(child)) {
                res = res.concat(child)
            } else {
                res.push(child)
            }
        })
        children = res
    }
    children = children.map((child: any) => {
        if (child) return child
        return {
            props: {
                value: ''
            }
        }
    })
    if (children.length > 1) {
        children.map((item: any) => {
            array.push(item.props.value)
        })
    } else if (children.length === 1) {
        array.push(children[0].props.value)
    } else {
        array.push(children.props.value);
    }
    return array;
}

// 函数式组件
const RadioGroupComponent: React.FC<RadioGroupProps> = (props) => {
    const {
        Component = 'div',
        children,
        clsPrefix = `${prefix}-radio-group`,
        className,
        disabled,
        readOnly = false,
        antd = false,
        options,
        optionType,
        spaceSize,
        maxCount: propMaxCount = false,
        tiled = false,
        locale,
        browser = false,
        browserShow = false,
        bordered,
        onChange = noop,
        name = '',
        defaultValue = '',
        selectedValue,
        value,
        onClick,
        size,
        dir,
        dropDrownProps,
        popoverProps,
        tiledFooter = null,
        showMoreText = false,
        browserClassName,
        browserStyle,
        ...others
    } = props;

    const initValue = value ?? (selectedValue ?? defaultValue);

    const [focusValue, setFocusValue] = useState<RadioValue>('');
    const [currentSelectedValue, setCurrentSelectedValue] = useState<RadioValue | React.ChangeEvent>(initValue);
    const [maxCount, setMaxCount] = useState(-1);
    const [popoverMaxWidth, setPopoverMaxWidth] = useState(100);
    const [isMaxCountInitialized, setIsMaxCountInitialized] = useState(false);

    // 所有useCallback hooks - 无条件调用
    const throttleRAF = useCallback((fn: () => void, delay: number = 16) => {
        let rafId: number | null = null;
        let timeoutId: any = null;
        let lastCall = 0;

        return () => {
            const now = Date.now();

            if (now - lastCall >= delay) {
                lastCall = now;
                if (rafId) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }
                rafId = requestAnimationFrame(fn);
            } else {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                timeoutId = setTimeout(() => {
                    lastCall = Date.now();
                    if (rafId) {
                        cancelAnimationFrame(rafId);
                    }
                    rafId = requestAnimationFrame(fn);
                }, delay - (now - lastCall));
            }
        };
    }, []);

    // Refs - 无条件调用
    const radioGroupRef = useRef<HTMLElement>(null);
    const parentResizeObserverRef = useRef<OrNull<ResizeObserver>>(null);
    const childNodesResizeObserverRef = useRef<OrNull<ResizeObserver>>(null);
    const dobounceFillSpaceFunRef = useRef<(() => void | undefined) | undefined | null>(null);
    const fullChildDimensionsRef = useRef<Array<{width: number; marginLeft: number; marginRight: number; offsetLeft: number}>>([]);

    // 缓存对象 - 无条件调用
    const dimensionCache = useRef<{
        parentWidth: number;
        childDimensions: Array<{width: number; marginLeft: number; marginRight: number; offsetLeft: number}>;
        lastUpdateTime: number;
        isValid: boolean;
    }>({
        parentWidth: 0,
        childDimensions: [],
        lastUpdateTime: 0,
        isValid: false
    });

    // 优化后的尺寸计算函数，添加缓存机制
    const updateDimensionCache = useCallback(() => {
        if (!radioGroupRef.current) return false;

        let parent = radioGroupRef.current.parentElement;
        if (parent && parent.nodeName.toUpperCase() === 'FORM') {
            parent = parent.parentNode as HTMLElement;
        }

        const parentWidth = parent?.clientWidth || 0;
        const childNodes = radioGroupRef.current.childNodes as any;
        const now = Date.now();

        // 检查缓存是否有效（500ms内的缓存认为有效，避免频繁重新计算）
        if (dimensionCache.current.isValid &&
            now - dimensionCache.current.lastUpdateTime < 500 &&
            dimensionCache.current.parentWidth === parentWidth &&
            dimensionCache.current.childDimensions.length === childNodes.length) {
            return true;
        }

        if (!parentWidth || childNodes.length === 0) {
            return false;
        }

        // 批量读取DOM属性，减少回流
        const childDimensions: Array<{width: number; marginLeft: number; marginRight: number; offsetLeft: number}> = [];

        // 一次性读取所有样式，避免多次触发回流
        for (let i = 0; i < childNodes.length; i++) {
            const child = childNodes[i];
            if (child && child.offsetWidth) {
                const style = getComputedStyle(child);
                childDimensions.push({
                    width: child.clientWidth,
                    marginLeft: parseInt(style.marginLeft) || 0,
                    marginRight: parseInt(style.marginRight) || 0,
                    offsetLeft: child.offsetLeft
                });
            }
        }

        // 如果当前测量的子项更多，则记录完整子项尺寸以供后续在收起后使用
        if (childDimensions.length >= fullChildDimensionsRef.current.length) {
            fullChildDimensionsRef.current = childDimensions.map(d => ({ width: d.width, marginLeft: d.marginLeft, marginRight: d.marginRight, offsetLeft: d.offsetLeft }));
        }

        // 更新缓存
        dimensionCache.current = {
            parentWidth,
            childDimensions,
            lastUpdateTime: now,
            isValid: true
        };

        return true;
    }, []);

    const setInnerMaxCount = useCallback(() => {
        if (!propMaxCount || !radioGroupRef.current) {
            return;
        }

        // 使用缓存的尺寸信息
        if (!updateDimensionCache()) {
            return;
        }

        const { parentWidth } = dimensionCache.current;
        let dropDownWidth = 20;
        let langTemp = getLangInfo(locale, i18n, 'calendar').lang;
        if (showMoreText) {
            switch (langTemp) {
                case 'en-us':
                    dropDownWidth = 63;
                    break;
                case 'vi-vn':
                    dropDownWidth = 92;
                    break;
                case 'id-id':
                    dropDownWidth = 109;
                    break;
                case 'ar-sa':
                    dropDownWidth = 52;
                    break;
                default:
                    dropDownWidth = 58;
                    break;
            }
        }

        const isRTL = dir === 'rtl';
        const effectiveDimensions = fullChildDimensionsRef.current.length ? fullChildDimensionsRef.current : dimensionCache.current.childDimensions;
        let calculatedMaxCount = effectiveDimensions.length;

        if (effectiveDimensions.length > 0) {
            if (isRTL) {
                let totalWidth = effectiveDimensions[0].width;
                for (let i = 1; i < effectiveDimensions.length; i++) {
                    const dim = effectiveDimensions[i];
                    totalWidth += dim.width + dim.marginLeft + dim.marginRight;
                    if (totalWidth > parentWidth) {
                        calculatedMaxCount = dropDownWidth > 90 ? i - 3 : (dropDownWidth > 40 ? i - 2 : i - 1);
                        break;
                    }
                }
                if (effectiveDimensions[0].width > parentWidth || effectiveDimensions[0].width > parentWidth) {
                    calculatedMaxCount = 0;
                }
            } else {
                // LTR：用初次测量的 offsetLeft 判定首行容量，再校验“更多”按钮是否可放
                const firstLeft = effectiveDimensions[0].offsetLeft;
                let firstRowCount = effectiveDimensions.length;
                for (let i = 1; i < effectiveDimensions.length; i++) {
                    const dim = effectiveDimensions[i];
                    if (Math.abs(dim.offsetLeft - firstLeft) < 5) {
                        firstRowCount = i;
                        break;
                    }
                }
                if (firstRowCount === effectiveDimensions.length) {
                    calculatedMaxCount = -1;
                } else {
                    const prev = effectiveDimensions[firstRowCount - 1];
                    const overflowWithDropdown = (prev.offsetLeft + prev.width + prev.marginRight + dropDownWidth + 3) > parentWidth;
                    if (overflowWithDropdown) {
                        calculatedMaxCount = dropDownWidth > 90 ? firstRowCount - 2 : (dropDownWidth > 40 ? firstRowCount - 1 : firstRowCount - 1);
                    } else {
                        calculatedMaxCount = firstRowCount;
                    }
                }
                if (effectiveDimensions[0].width > parentWidth || effectiveDimensions[0].width + effectiveDimensions[0].marginRight > parentWidth) {
                    calculatedMaxCount = 0;
                }
            }
        }

        // 只有当需要隐藏某些项目时才设置maxCount
        const newMaxCount = calculatedMaxCount < effectiveDimensions.length ? calculatedMaxCount : -1;

        // 避免不必要的状态更新，减少闪动
        if (maxCount !== newMaxCount) {
            setMaxCount(newMaxCount);
        }

        // 标记初始化完成
        if (!isMaxCountInitialized) {
            setIsMaxCountInitialized(true);
        }
    }, [propMaxCount, updateDimensionCache, maxCount, isMaxCountInitialized, locale, showMoreText, dir]);

    const getMaxCount = useCallback(() => {
        // 将条件逻辑放在函数内部，确保useCallback始终被调用
        if (propMaxCount === true && !browser && radioGroupRef.current) {
            // 使用优化后的RAF节流函数
            dobounceFillSpaceFunRef.current = throttleRAF(() => setInnerMaxCount(), 200);

            let parent = radioGroupRef.current.parentNode;
            if (parent && parent.nodeName.toUpperCase() === 'FORM') {
                parent = parent.parentNode;
            }
            if (parent) {
                parentResizeObserverRef.current = new ResizeObserver((entries) => {
                    // 优化：只在尺寸真正变化时才重新计算
                    const entry = entries[0];
                    if (entry && entry.contentRect) {
                        const newWidth = entry.contentRect.width;
                        const cachedWidth = dimensionCache.current.parentWidth;

                        // 只有当宽度真正变化且超过阈值时才重新计算，避免微小变化导致的频繁更新
                        if (Math.abs(newWidth - cachedWidth) > 5) {
                            // 使用RAF确保在下一帧执行，避免在布局阶段操作DOM
                            requestAnimationFrame(() => {
                                // 只清除缓存，不立即设置maxCount，避免状态跳跃
                                dimensionCache.current.isValid = false;
                                setMaxCount(-1);
                                // 使用节流函数进行延迟计算
                                dobounceFillSpaceFunRef.current && dobounceFillSpaceFunRef.current();
                            });
                        }
                    }
                });
                parentResizeObserverRef.current.observe(parent as Element);
            }
            if (tiled && radioGroupRef.current) {
                childNodesResizeObserverRef.current = new ResizeObserver(entries => {
                    for (let entry of entries) {
                        if (entry.target === radioGroupRef.current) {
                            setPopoverMaxWidth(entry.contentRect.width - 3);
                        }
                    }
                });
                childNodesResizeObserverRef.current.observe(radioGroupRef.current);
            }
        }
    }, [propMaxCount, browser, tiled, throttleRAF, setInnerMaxCount]);

    // 更新按钮标签的函数
    const updateButtonLabels = useCallback(() => {
        if (!radioGroupRef.current) return;
        const hasSiblings = radioGroupRef.current.previousElementSibling || radioGroupRef.current.nextElementSibling;
        const parentWidth = radioGroupRef.current.parentElement?.clientWidth || 400;
        if (!hasSiblings && parentWidth > 100) {
            const maxButtonWidth = Math.min(parentWidth * 0.4, 80);
            const buttons = radioGroupRef.current.querySelectorAll('.wui-radio-button');
            buttons.forEach((button) => {
                if (button instanceof HTMLElement) {
                    const labelElement = button.querySelector('.wui-radio-button-label');
                    const inputElement = button.querySelector('input[type="radio"]');
                    if (labelElement instanceof HTMLElement && inputElement instanceof HTMLElement) {
                        const isOnlyText = (node: Node): boolean => {
                            const children = Array.from(node.childNodes);
                            if (children.length === 1 && children[0].nodeType === Node.TEXT_NODE) {
                                return true;
                            }
                            return false;
                        };
                        if (isOnlyText(labelElement)) {
                            labelElement.classList.add(`${prefix}-radio-button-label-is-text`);
                        }
                        const fullText = labelElement.textContent || '';
                        labelElement.style.maxWidth = `${maxButtonWidth}px`;
                        labelElement.setAttribute('title', fullText);
                        inputElement.setAttribute('title', fullText);
                    }
                }
            });
        }
    }, []);

    // 处理值改变
    const handleChange = useCallback((value: RadioValue | React.ChangeEvent, event?: React.ChangeEvent, options?: {maxCount?: number}) => {
        let val = value
        let currentVal = antd ? (value as React.ChangeEvent<HTMLInputElement>).target?.value : value;
        onClick && onClick(value, event)

        if (currentVal === selectedValue) return;
        if (!('selectedValue' in props)) {
            setCurrentSelectedValue(val)
        }
        const args: [value: RadioValue | React.ChangeEvent | React.MouseEvent<HTMLElement>, event?: | React.ChangeEvent | React.MouseEvent<HTMLElement>, options?: {maxCount?: number}] = [value, event];
        if (options) {
            args.push(options)
        }
        onChange && onChange(...args);
    }, [antd, onClick, selectedValue, props, onChange]);

    // 渲染选项内容
    const renderOptionContent = useCallback(({ option, mergeStyle }: { option: (RadioProps & { label: ReactNode } | string); mergeStyle: CSSProperties }) => {
        const radioProps = {
            readOnly: readOnly || (browser && browserShow),
            disabled: disabled,
            antd: antd
        };
        if (typeof option === 'string') {
            return optionType === "button" ? <RadioButton {...radioProps} value={option} style={mergeStyle}>
                {option}
            </RadioButton> : <Radio value={option} {...radioProps}>{option}</Radio>;
        }
        const { value, label, style, ...otherProps } = option;
        return optionType === "button" ? <RadioButton value={value} style={mergeStyle} {...radioProps} {...otherProps} >
            {label}
        </RadioButton> : <Radio value={value} {...radioProps} {...otherProps}>{label}</Radio>;
    }, [optionType, readOnly, browser, browserShow, disabled, antd]);

    // 渲染子组件内容
    const renderChildContent = useCallback(({ option, mergeStyle }: { option: ReactElement<RadioProps>; mergeStyle: CSSProperties }) => {
        return React.cloneElement(option,
            {
                disabled: option?.props?.disabled || disabled,
                readOnly: readOnly || (browser && browserShow),
                antd,
                style: mergeStyle
            }
        )
    }, [disabled, readOnly, browser, browserShow, antd]);

    // 渲染内容从计数
    const renderContentFromCount = useCallback(({ spaceStyle, menuKey, menuContent, style, arrayLength, optionType, option, index, isOption, menuItem }: {
        spaceStyle: CSSProperties; menuKey: string; menuContent: string; style: CSSProperties; arrayLength: number; optionType?: string; option?: ReactElement<RadioProps> | RadioProps | string; index: number; isOption: boolean; menuItem: any[];
    }) => {
        let mergeStyle = spaceStyle;
        mergeStyle = style || {};
        if (index !== arrayLength - 1 && spaceStyle) {
            mergeStyle = { ...spaceStyle, ...mergeStyle }
        }
        if (!propMaxCount || optionType !== "button") {
            return isOption ? renderOptionContent({ option, mergeStyle } as { option: (RadioProps & { label: ReactNode } | string); mergeStyle: CSSProperties }) : renderChildContent({ option, mergeStyle } as { option: ReactElement<RadioProps>; mergeStyle: CSSProperties })
        }
        if (typeof propMaxCount === 'number' || (typeof maxCount === 'number' && maxCount > -1)) {
            const mergeMaxCount = maxCount === 0 ? maxCount : (maxCount || propMaxCount as number)
            if ((mergeMaxCount > 0 && mergeMaxCount < arrayLength && index >= mergeMaxCount) || mergeMaxCount === 0) {
                menuItem.push(
                    <Item
                        key={menuKey as string}
                        className={classnames({
                            'is-checked': currentSelectedValue === menuKey,
                            [`${prefix}-radio-item-is-checked`]: currentSelectedValue === menuKey,
                            [`${prefix}-radio-readonly`]: readOnly || (typeof option === 'object' && 'readOnly' in option && option.readOnly),
                            [`${prefix}-radio-disabled`]: disabled || (typeof option === 'object' && 'disabled' in option && option.disabled),
                        })}
                        disabled={disabled || (typeof option === 'object' && 'disabled' in option && option.disabled)}
                    >
                        {menuContent}
                    </Item>
                )
                return null
            } else {
                return isOption ? renderOptionContent({ option, mergeStyle } as { option: (RadioProps & { label: ReactNode } | string); mergeStyle: CSSProperties }) : renderChildContent({ option, mergeStyle } as { option: ReactElement<RadioProps>; mergeStyle: CSSProperties })
            }
        } else {
            return isOption ? renderOptionContent({ option, mergeStyle } as { option: (RadioProps & { label: ReactNode } | string); mergeStyle: CSSProperties }) : renderChildContent({ option, mergeStyle } as { option: ReactElement<RadioProps>; mergeStyle: CSSProperties })
        }
    }, [propMaxCount, maxCount, currentSelectedValue, readOnly, disabled, renderOptionContent, renderChildContent]);

    // 渲染选项
    const renderOptions = useCallback((options: RadioGroupProps['options'], { spaceStyle, menuItem }: { spaceStyle: CSSProperties; menuItem: any[]; }) => {
        const radioChilds = options!.map((option, index) => {
            if (!option) {
                return null
            }
            let style = {};
            let menuKey = option as string;
            let menuContent = option as string;
            if (typeof option !== 'string') {
                style = option.style || {};
                menuKey = option.value as string;
                menuContent = option.label as string;
            }
            return renderContentFromCount({ spaceStyle, menuKey, menuContent, style, arrayLength: options?.length!, optionType, option, index, isOption: true, menuItem })
        })
        return radioChilds
    }, [optionType, renderContentFromCount]);

    // 渲染子组件
    const renderChilds = useCallback((children: ReactElement<RadioProps>, { spaceStyle, menuItem }: { spaceStyle: CSSProperties; menuItem: any[] }) => {
        const childsNum = React.Children.count(children);
        return React.Children.map(children, (child, index) => {
            if (!child) {
                return null
            }
            const { style, clsPrefix, value, children } = child?.props;
            let mergeStyle = style || {};
            const menuKey = value as string;
            const menuContent = children as string;
            const optionType = clsPrefix === `${prefix}-radio-button` ? 'button' : '';
            return renderContentFromCount({ spaceStyle, menuKey, menuContent, style: mergeStyle, arrayLength: childsNum, optionType, option: child, index, isOption: false, menuItem })
        })
    }, [renderContentFromCount]);

    // 下拉选择项目
    const selectDropItem = useCallback(({domEvent, selectedKeys: [selectedKey]} : {domEvent: any, selectedKeys: string[]}) => {
        if (!readOnly && !disabled) {
            handleChange(selectedKey, domEvent as any, {maxCount: maxCount})
        }
    }, [readOnly, disabled, handleChange, maxCount]);

    // Move selectedText hooks outside of renderBrowserOpts and renderBrowserBtns

    // For options
    const selectedTextForOptions = useMemo(() => {
        if (!options) return '';
        const selectedOption = options.find((option: any) => {
            if (typeof option === 'string') {
                return option === currentSelectedValue;
            }
            return option?.value === currentSelectedValue;
        });
        if (selectedOption) {
            if (typeof selectedOption === 'string') {
                return selectedOption as string;
            } else {
                return (selectedOption as any).label as string || '';
            }
        }
        return '';
    }, [options, currentSelectedValue]);

    // For children (RadioButton)
    const selectedTextForChildren = useMemo(() => {
        let text = '';
        React.Children.forEach(children, (child: any) => {
            if (!child || !child.props) return;
            if (child.props.value === currentSelectedValue) {
                const childContent = child.props.children;
                if (typeof childContent === 'string' || typeof childContent === 'number') {
                    text = String(childContent);
                } else if (React.isValidElement(childContent)) {
                    const extractText = (element: React.ReactNode): string => {
                        if (typeof element === 'string' || typeof element === 'number') {
                            return String(element);
                        } else if (React.isValidElement(element)) {
                            return extractText(element.props.children);
                        } else if (Array.isArray(element)) {
                            return element.map(extractText).join('');
                        }
                        return '';
                    };
                    text = extractText(childContent);
                }
            }
        });
        return text;
    }, [children, currentSelectedValue]);

    // Update renderBrowserOpts and renderBrowserBtns to not use hooks
    const renderBrowserOpts = useCallback((selectedText: string) => {
        const wrapperCls = classnames(`${prefix}-component-browser`, {
            [`${prefix}-component-browser-border-bottom`]: bordered === 'bottom',
            [`${prefix}-component-browser-${size}`]: !!size
        })

        const componentCls = classnames(`${clsPrefix}-browser`, {
            [`${browserClassName}`]: browserClassName
        })
        return (
            <div className={wrapperCls}>
                <div className={componentCls} style={browserStyle} fieldid={props.fieldid ? props.fieldid : undefined}>
                    {selectedText || '--'}
                </div>
            </div>
        );
    }, [clsPrefix, bordered, browserClassName, browserStyle, size]);

    const renderBrowserBtns = useCallback((selectedText: string) => {
        const wrapperCls = classnames(`${prefix}-component-browser`, {
            [`${prefix}-component-browser-border-bottom`]: bordered === 'bottom',
            [`${prefix}-component-browser-${size}`]: !!size
        })

        const componentCls = classnames(`${clsPrefix}-browser`, {
            [`${browserClassName}`]: browserClassName
        })
        return (
            <div className={wrapperCls}>
                <div className={componentCls} style={browserStyle}>
                    {selectedText || '--'}
                </div>
            </div>
        );
    }, [clsPrefix, bordered, browserClassName, browserStyle, size]);

    // Context 值
    const contextValue = useMemo(() => {
        const value = {
            name,
            selectedValue: currentSelectedValue as RadioValue,
            onChange: handleChange,
            size,
            focusValue
        };

        return value;
    }, [name, currentSelectedValue, handleChange, size, focusValue]);

    // 处理 selectedValue 和 value 的更新
    useEffect(() => {
        // 修复value处理逻辑：如果props中有value属性，则始终使用value（包括undefined/null），否则使用selectedValue
        const newValue = 'value' in props ? value : (selectedValue ?? defaultValue);

        setCurrentSelectedValue(newValue as RadioValue);

        const array = getValues(children);
        if (array.indexOf(selectedValue as RadioValue) === -1 ||
            (value && array.indexOf(value) === -1)) {
            setFocusValue(array[0]);
        } else {
            setFocusValue('');
        }
    }, [selectedValue, value, children, props, defaultValue]);

    // 初始化和挂载逻辑
    useEffect(() => {
        // eslint-disable-next-line camelcase
        let children_inner = children
        let array = getValues(children_inner);
        if (array.indexOf(selectedValue as RadioValue) == -1) {
            setFocusValue(array[0]);
        }
        getMaxCount();
        // 使用 requestAnimationFrame 确保在组件挂载后执行
        requestAnimationFrame(() => {
            updateButtonLabels();
        });
    }, [children, selectedValue, getMaxCount, updateButtonLabels]);

    // 处理属性变化 - 无条件调用useEffect
    useEffect(() => {
        // 将条件逻辑移到useEffect内部
        const shouldUpdateLabels = browser !== undefined || options !== undefined || children !== undefined;
        const shouldGetMaxCount = browser === false && propMaxCount === true;

        if (shouldUpdateLabels) {
            requestAnimationFrame(() => {
                updateButtonLabels();
            });
        }

        if (shouldGetMaxCount) {
            requestAnimationFrame(() => {
                getMaxCount();
            });
        }
    }, [browser, options, children, propMaxCount, updateButtonLabels, getMaxCount]);

    // 清理函数
    useEffect(() => {
        return () => {
            dobounceFillSpaceFunRef.current = null;
            if (parentResizeObserverRef.current) {
                parentResizeObserverRef.current.disconnect();
            }
            if (childNodesResizeObserverRef.current) {
                childNodesResizeObserverRef.current.disconnect();
            }
        };
    }, []);

    // 计算所有需要的变量，无论哪种渲染模式都计算
    let spaceCls;
    let btnsCls;
    let browserCls = '';
    let spaceStyle: CSSProperties = {};
    const isRTL = dir === 'rtl';
    if (spaceSize) {
        if (typeof spaceSize === 'string' && Object.keys(sizeMap).includes(spaceSize)) {
            spaceCls = `${clsPrefix}-space-${setComponentSize(spaceSize, {})}`
        } else {
            const spaceNum = typeof spaceSize === 'number' ? spaceSize : spaceSize?.endsWith?.('px') ? parseInt(spaceSize) : 0;
            if (!isNaN(spaceNum) && spaceNum) {
                spaceStyle = isRTL ? { marginLeft: `${spaceNum}px` } : { marginRight: `${spaceNum}px` };
            }
        }
    }
    let radioChilds: ReactNode = null;
    let moreChild: ReactNode = null;
    const menuItem: any[] = [];
    if (options?.length) {
        btnsCls = `${clsPrefix}-opts`
        radioChilds = renderOptions(options, { spaceStyle, menuItem })
    } else {
        btnsCls = `${clsPrefix}-btns`
        radioChilds = renderChilds(children as ReactElement<RadioProps>, { spaceStyle, menuItem })
    }
    if (browser) {
        browserCls = `${clsPrefix}-browser`;
    }

    // dropdown 内容
    if (menuItem.length) {
        let getMore = false;
        getMore = menuItem.some(obj => Object.values(obj).includes(currentSelectedValue));
        const inner = tiled ? (
            <div className={`${clsPrefix}-tiled-wrapper`}>
                {
                    menuItem.map((item, index) => {
                        let key : string = item.key;
                        return (
                            <span
                                onClick={(e: React.MouseEvent<HTMLElement>) => selectDropItem({domEvent: e, selectedKeys: [key]})}
                                key={index}
                                className={classnames({
                                    [`${clsPrefix}-tiled-item`]: true,
                                    [`${clsPrefix}-tiled-item-${size}`]: size !== 'default',
                                    "is-checked": currentSelectedValue === item.key,
                                    [`${clsPrefix}-tiled-is-checked`]: currentSelectedValue === item.key,
                                    [`${clsPrefix}-tiled-item-read-only`]: readOnly,
                                    "disabled": disabled
                                })}
                                style={{
                                    maxWidth: (popoverMaxWidth || 200) - 16,
                                }}
                                title={typeof item.props.children === 'string' ? item.props.children : undefined}
                            >
                                {item.props.children}
                            </span>
                        )
                    })
                }
                {tiledFooter ?
                    <div className={`${clsPrefix}-tiled-footer`}>{tiledFooter}</div> : null}
            </div>
        ) : <Menu selectedKeys={[]} onSelect={selectDropItem}>{menuItem}</Menu>
        moreChild = tiled ? (
            <Popover placement='bottomRight' overlayStyle={{ maxWidth: popoverMaxWidth }} {...popoverProps} content={inner} trigger='click' getPopupContainer={() => radioGroupRef.current as HTMLElement}>
                <Button
                    className={classnames({
                        [`${clsPrefix}-more-button`]: true,
                        "is-checked": getMore,
                        [`${clsPrefix}-more-button-is-checked`]: getMore,
                        "show-text": showMoreText,
                        [`${clsPrefix}-has-read-only`]: readOnly,
                        [`${clsPrefix}-has-disabled`]: disabled,
                    })}
                    size={!size || size === 'default' ? undefined : size}
                >{showMoreText && <span className={`${clsPrefix}-more-button-span`}>{getLangInfo(locale, i18n, 'calendar').langMap.more}</span>}<Icon type="uf-arrow-down" /></Button>
            </Popover>
        ) : (
            <Dropdown {...dropDrownProps} overlay={inner} overlayClassName={`${clsPrefix}-my-radio-dropdown-menu`}>
                <Button
                    className={classnames({
                        [`${clsPrefix}-more-button`]: true,
                        "is-checked": getMore,
                        [`${clsPrefix}-more-button-is-checked`]: getMore,
                        "show-text": showMoreText,
                        [`${clsPrefix}-has-read-only`]: readOnly,
                        [`${clsPrefix}-has-disabled`]: disabled,
                    })}
                    size={!size || size === 'default' ? undefined : size}
                >{showMoreText && <span className={`${clsPrefix}-more-button-span`}>{getLangInfo(locale, i18n, 'calendar').langMap.more}</span>}<Icon type="uf-arrow-down" /></Button>
            </Dropdown>
        )
    }
    const hasNumericSpace = typeof spaceSize === 'number' && spaceSize > 0;
    const classNames = classnames(
        clsPrefix,
        className,
        spaceCls,
        btnsCls,
        browserCls,
        { [`${clsPrefix}-numeric-space`]: hasNumericSpace },
        // 添加初始化状态的类名，避免闪动
        { [`${clsPrefix}-max-count-initializing`]: propMaxCount && !isMaxCountInitialized }
    )

    // 检查是否需要渲染浏览器模式 - 在所有hooks调用之后进行条件返回
    const shouldRenderBrowserOpts = browser && options && options.length && (optionType === 'button' || !browserShow);
    const shouldRenderBrowserBtns = browser && children && Array.isArray(children) && children.length > 0 && (children[0].type === RadioButton || !browserShow);

    // 处理browser模式的早期返回，放在所有Hooks调用之后
    if (shouldRenderBrowserOpts) {
        return (
            <RadioGroupContext.Provider value={contextValue}>
                {renderBrowserOpts(selectedTextForOptions)}
            </RadioGroupContext.Provider>
        );
    } else if (shouldRenderBrowserBtns) {
        return (
            <RadioGroupContext.Provider value={contextValue}>
                {renderBrowserBtns(selectedTextForChildren)}
            </RadioGroupContext.Provider>
        );
    }

    // 正常模式渲染
    return (
        <RadioGroupContext.Provider value={contextValue}>
            <Component className={classNames} {...(omit as any)(others, ['onChange', 'selectedValue', 'onClick'])} focusValue={focusValue} ref={radioGroupRef}>
                {radioChilds}
                {propMaxCount ? moreChild : null}
            </Component>
        </RadioGroupContext.Provider>
    );
}

// 导出带有HOC包装的组件
const RadioGroup = WithConfigConsumer()(WebUI({ name: "radio-group" })(RadioGroupComponent));

export default RadioGroup as React.ComponentClass<Partial<RadioGroupProps>>;
